import React, { useState, memo, useEffect, useRef } from 'react';
import useDebounce from '../../utils/useDebounce';
import ReturnsIframeRenderer from '../../components/ui/ReturnsIframeRenderer';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { userService } from '../../services/userService';
import SellerProductManagement from './components/SellerProductManagement';
import SellerComponentManagement from './components/SellerComponentManagement';
import SellerDashboardStats from './components/SellerDashboardStats';
import SellerOrderList from './components/SellerOrderList';
import SellerPaymentDetails from './components/SellerPaymentDetails';
import SellerSettingsTab from './components/SellerSettingsTab';
import MyStoreTab from './components/MyStoreTab';
import OverviewTab from './components/OverviewTab';
import StoreReviewsTab from './components/StoreReviewsTab';
import InventoryUploadTab from './components/InventoryUploadTab';
import CouponManagement from './components/CouponManagement';
import SellerBannerAdsTab from './components/SellerBannerAdsTab';

const MemoizedSellerProductManagement = memo(SellerProductManagement);
const MemoizedSellerComponentManagement = memo(SellerComponentManagement);
const MemoizedSellerDashboardStats = memo(SellerDashboardStats);
const MemoizedSellerOrderList = memo(SellerOrderList);
const MemoizedSellerPaymentDetails = memo(SellerPaymentDetails);
const MemoizedMyStoreTab = memo(MyStoreTab);
const MemoizedOverviewTab = memo(OverviewTab);
const MemoizedStoreReviewsTab = memo(StoreReviewsTab);
const MemoizedInventoryUploadTab = memo(InventoryUploadTab);
const MemoizedCouponManagement = memo(CouponManagement);

const SellerAccountDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const iframeRef = useRef(null); // Ref for the iframe
  const [searchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const normalizedInitialTab = (rawTab === 'payments' || rawTab === 'payouts') 
    ? 'statements' 
    : (rawTab === 'pendingOrders' || rawTab === 'completedOrders') 
      ? 'orders' 
      : rawTab;
  const [activeTab, setActiveTab] = useState(normalizedInitialTab || 'overview'); // Default to overview tab
  const [orderStatusFilter, setOrderStatusFilter] = useState(rawTab === 'completedOrders' ? 'completed' : 'pending');
  const [initialComponentId, setInitialComponentId] = useState(null);
  const [returnsIframeHeight, setReturnsIframeHeight] = useState('100%');
  const [accountStatus, setAccountStatus] = useState('Active');
  const [sellerAccount, setSellerAccount] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  // Sync activeTab with URL
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      if (tabParam === 'completedOrders') {
        setOrderStatusFilter('completed');
      } else if (tabParam === 'pendingOrders') {
        setOrderStatusFilter('pending');
      }
      const normalizedTab = (tabParam === 'payments' || tabParam === 'payouts') 
        ? 'statements' 
        : (tabParam === 'pendingOrders' || tabParam === 'completedOrders') 
          ? 'orders' 
          : tabParam;
      if (normalizedTab !== activeTab) {
        setActiveTab(normalizedTab);
      }
    }
  }, [searchParams]);

  // Track subtab view
  useEffect(() => {
    if (user?.id && activeTab) {
      userService.recordActivity(
        user.id,
        navigator.userAgent,
        'Subtab View',
        `/seller-dashboard/${activeTab}`
      ).catch(err => console.warn('Activity record failed', err));
    }
  }, [activeTab, user?.id]);


  const fetchAccountData = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('seller_accounts')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (data) {
        setSellerAccount(data);
        setAccountStatus(data.account_status);
      }
    } catch (err) {
      console.error('Error fetching account status:', err);
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    fetchAccountData();
  }, [user]);

  useEffect(() => {
    // Listener for messages from iframe
    const handleMessage = (event) => {
      // Handle resize messages
      if (event.data && event.data.type === 'returns-portal-resize' && event.data.height) {
        setReturnsIframeHeight(`${event.data.height}px`);
      }
      
      // Handle ready message
      if (event.data && event.data.type === 'returns-portal-ready') {
        postSession();
      }
    };

    const postSession = async () => {
      const iframe = iframeRef.current;
      if (!iframe || !iframe.contentWindow) return;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const sessionPayload = {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        expires_in: session.expires_in,
        expires_at: session.expires_at,
        token_type: 'bearer',
        user: session.user
      };

      iframe.contentWindow.postMessage(
        { type: 'supabase-session', payload: sessionPayload },
        '*'
      );
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [activeTab, user]);

  // Handle iframe load event as a fallback
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      if (activeTab === 'returns') {
        // Optional: could trigger postSession here too if ready message isn't enough
      }
    };

    iframe.addEventListener('load', handleLoad);
    return () => iframe.removeEventListener('load', handleLoad);
  }, [iframeRef.current, activeTab]);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
    }
  }, [user, navigate]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="Briefcase" size={24} />
            </div>
            <div>
             <h1 className="text-3xl font-bold text-foreground">Seller Dashboard</h1>
             <p className="text-muted-foreground">Manage your products, components, reviews, and store credentials.</p>
            </div>
          </div>

          {!statusLoading && accountStatus !== 'Active' && accountStatus !== 'Pending' && (
            <div className="mb-8 bg-destructive/10 border border-destructive/20 rounded-lg p-6 flex items-start gap-4">
              <div className="w-10 h-10 bg-destructive/20 text-destructive rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-destructive mb-1">Store Front Temporarily Suspended</h3>
                <p className="text-destructive/80">
                  Your store has been temporarily suspended for the following: <span className="font-semibold">{accountStatus}</span>. 
                  Please contact support to resolve these issues and reactivate your store.
                </p>
              </div>
            </div>
          )}

          <div className="mb-8">
            <SellerDashboardStats sellerId={sellerAccount?.id} activeTab={activeTab} />
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-border mb-6">
            <nav className="-mb-px flex justify-center space-x-8 overflow-x-auto" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('overview')}
                className={`
                  ${activeTab === 'overview'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                `}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('myStore')}
                className={`
                  ${activeTab === 'myStore'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                `}
              >
                My Store
              </button>
              <button
                onClick={() => setActiveTab('products')}
                className={`
                  ${activeTab === 'products'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                `}
              >
                My Products
              </button>
              <button
                onClick={() => setActiveTab('components')}
                className={`
                  ${activeTab === 'components'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                `}
              >
                My Components
              </button>
              <button
                onClick={() => setActiveTab('orders')}
                className={`
                  ${(activeTab === 'orders' || activeTab === 'pendingOrders' || activeTab === 'completedOrders')
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                `}
              >
                Orders
              </button>
              <button
                onClick={() => setActiveTab('returns')}
                className={`
                  ${activeTab === 'returns'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                `}
              >
                Returns
              </button>
              <button
                onClick={() => setActiveTab('statements')}
                className={`
                  ${(activeTab === 'statements' || activeTab === 'payments' || activeTab === 'payouts')
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                `}
              >
                Statements
              </button>
              <button
                onClick={() => setActiveTab('bannerAds')}
                className={`
                  ${(activeTab === 'bannerAds' || activeTab === 'ads' || activeTab === 'advertising')
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                `}
              >
                Ad Impressions
              </button>
              <button
                onClick={() => setActiveTab('storeReviews')}
                className={`
                  ${activeTab === 'storeReviews'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                `}
              >
                Store Reviews
              </button>
              <button
                onClick={() => setActiveTab('inventoryUpload')}
                className={`
                  ${activeTab === 'inventoryUpload'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                `}
              >
                Inventory Upload
              </button>
              <button
                onClick={() => setActiveTab('coupons')}
                className={`
                  ${activeTab === 'coupons'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                `}
              >
                Coupons
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`
                  ${activeTab === 'settings'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                `}
              >
                Settings
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div>
            {activeTab === 'overview' && (
              <MemoizedOverviewTab setActiveTab={setActiveTab} setInitialComponentId={setInitialComponentId} />
            )}
            {activeTab === 'myStore' && (
              <MemoizedMyStoreTab setActiveTab={setActiveTab} />
            )}
            {activeTab === 'products' && (
              <MemoizedSellerProductManagement />
            )}
            {activeTab === 'components' && (
              <MemoizedSellerComponentManagement initialSelectedId={initialComponentId} clearInitialSelectedId={() => setInitialComponentId(null)} />
            )}
            {(activeTab === 'orders' || activeTab === 'pendingOrders' || activeTab === 'completedOrders') && (
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-border">
                  <div className="flex items-center bg-muted/60 p-1 rounded-xl border border-border/50">
                    <button
                      type="button"
                      onClick={() => setOrderStatusFilter('pending')}
                      className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${
                        orderStatusFilter === 'pending'
                          ? 'bg-background text-primary shadow-xs border border-border'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Pending Orders
                    </button>
                    <button
                      type="button"
                      onClick={() => setOrderStatusFilter('completed')}
                      className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${
                        orderStatusFilter === 'completed'
                          ? 'bg-background text-primary shadow-xs border border-border'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Completed Orders
                    </button>
                  </div>
                </div>
                <MemoizedSellerOrderList sellerId={user?.id} statusFilter={orderStatusFilter} />
              </div>
            )}
            {(activeTab === 'statements' || activeTab === 'payments' || activeTab === 'payouts') && (
              <MemoizedSellerPaymentDetails sellerId={sellerAccount?.id} />
            )}
            {(activeTab === 'bannerAds' || activeTab === 'ads' || activeTab === 'advertising') && (
              <SellerBannerAdsTab sellerAccount={sellerAccount} user={user} />
            )}
            {activeTab === 'storeReviews' && (
              <MemoizedStoreReviewsTab sellerId={user?.id} />
            )}
            {activeTab === 'inventoryUpload' && (
              <MemoizedInventoryUploadTab sellerAccount={sellerAccount} setActiveTab={setActiveTab} onAccountUpdate={fetchAccountData} />
            )}
            {activeTab === 'coupons' && (
              <MemoizedCouponManagement sellerAccount={sellerAccount} setActiveTab={setActiveTab} onAccountUpdate={fetchAccountData} />
            )}
            {activeTab === 'settings' && (
              <SellerSettingsTab onAccountUpdate={fetchAccountData} />
            )}
            {activeTab === 'returns' && (
              <ReturnsIframeRenderer iframeHeight={returnsIframeHeight} isUserDashboard={false} iframeRef={iframeRef} />
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SellerAccountDashboard;